import React from 'react';
import { useTranslation } from '../../../translations';
import { planGroupHasSizes } from '../utils';
import {
  PlanBody, PlanSections, PrepareBox, PlanHeading, PlanCount, PlanHeadingNote, PrepareList, PrepareRow,
  PrepareType, SizeChips, SizeChip, PlanNote, PlanSection, PlanCards, PlanEmpty, ReturnsTodayList,
} from './dayPlan.styles';

// U.29: plan wybranego dnia zamiast jednej listy rezerwacji. Trzy sekcje z licznikami (wydania,
// zwroty, trwające), a nad nimi „Do przygotowania": sprzęt z wydań tego dnia zliczony po typie
// i rozmiarze. Dane liczy czyste calendar/utils.js#buildDayPlan; ten komponent tylko rysuje.
// Kartę rezerwacji podaje ekran kalendarza (`renderCard`), ta sama co w „Nadchodzących"
// i w historii, więc klik otwiera szczegóły jak dotąd.
//
// `equipmentState`: 'loading' | 'ready' | 'failed'. Dopóki magazyn się nie wczytał, znamy tylko
// LICZBĘ sztuk (rezerwacja niesie same id), więc rozbicie na typy pojawia się dopiero przy 'ready'.
export default function DayPlan({ plan, equipmentState, renderCard }) {
  const { t } = useTranslation();
  const { prepare } = plan;

  // N.1: pod wydaniem sztuki, które tego dnia dopiero wracają od poprzedniego klienta.
  const returnsToday = (entry) => {
    const items = entry.items.filter((item) => item.returnsToday);
    if (items.length === 0) return null;
    return (
      <ReturnsTodayList data-cy="day-plan-returns-today">
        {items.map((item) => (
          <li key={item.id}>
            {`${[item.name, item.size].filter(Boolean).join(' ') || t('calendar.dayPlan.unknownItem', { id: item.id })} · ${t('calendar.dayPlan.itemReturnsToday')}`}
          </li>
        ))}
      </ReturnsTodayList>
    );
  };

  const section = (key, title, emptyText, entries) => (
    <PlanSection data-cy={`day-plan-${key}`}>
      <PlanHeading>
        {title}
        <PlanCount $empty={entries.length === 0} data-cy={`day-plan-count-${key}`}>{entries.length}</PlanCount>
      </PlanHeading>
      {entries.length > 0
        ? (
          <PlanCards>
            {entries.map((entry) => (
              <React.Fragment key={entry.reservation.id}>
                {renderCard(entry.reservation)}
                {key === 'pickups' && returnsToday(entry)}
              </React.Fragment>
            ))}
          </PlanCards>
        )
        : <PlanEmpty>{emptyText}</PlanEmpty>}
    </PlanSection>
  );

  return (
    <PlanBody data-cy="day-plan">
      {prepare.total > 0 && (
        <PrepareBox data-cy="day-plan-prepare">
          <PlanHeading>
            {t('calendar.dayPlan.prepare')}
            <PlanHeadingNote data-cy="day-plan-prepare-total">
              {t('calendar.dayPlan.pieces', { count: prepare.total })}
            </PlanHeadingNote>
          </PlanHeading>
          {prepare.returningToday > 0 && (
            <PlanNote style={{ marginTop: 0, marginBottom: 10 }} data-cy="day-plan-prepare-returning">
              {t('calendar.dayPlan.prepareReturningToday', { count: prepare.returningToday })}
            </PlanNote>
          )}
          {equipmentState === 'ready' && (
            <PrepareList>
              {prepare.groups.map((group) => (
                <PrepareRow key={group.type || ''} data-cy="day-plan-prepare-type">
                  <PrepareType>
                    <span className="type-name">{group.type || t('calendar.dayPlan.noType')}</span>
                    <span className="type-count">×{group.count}</span>
                  </PrepareType>
                  {planGroupHasSizes(group) && (
                    <SizeChips>
                      {group.sizes.map((s) => (
                        <SizeChip key={s.size || ''} $muted={s.size === null} data-cy="day-plan-prepare-size">
                          {s.size || t('calendar.dayPlan.noSize')} <b>×{s.count}</b>
                        </SizeChip>
                      ))}
                    </SizeChips>
                  )}
                </PrepareRow>
              ))}
              {prepare.unknown > 0 && (
                <PrepareRow data-cy="day-plan-prepare-unknown">
                  <PrepareType>
                    <span className="type-name">{t('calendar.dayPlan.otherEquipment')}</span>
                    <span className="type-count">×{prepare.unknown}</span>
                  </PrepareType>
                </PrepareRow>
              )}
            </PrepareList>
          )}
          {equipmentState === 'loading' && (
            <PlanNote style={{ marginTop: 0 }}>{t('calendar.dayPlan.equipmentLoading')}</PlanNote>
          )}
          {equipmentState === 'failed' && (
            <PlanNote style={{ marginTop: 0 }} data-cy="day-plan-equipment-failed">
              {t('calendar.dayPlan.equipmentFailed')}
            </PlanNote>
          )}
        </PrepareBox>
      )}

      <PlanSections data-cy="day-plan-sections">
        {section('pickups', t('calendar.dayPlan.pickups'), t('calendar.dayPlan.pickupsEmpty'), plan.pickups)}
        {section('returns', t('calendar.dayPlan.returns'), t('calendar.dayPlan.returnsEmpty'), plan.returns)}
        {section('ongoing', t('calendar.dayPlan.ongoing'), t('calendar.dayPlan.ongoingEmpty'), plan.ongoing)}
      </PlanSections>
    </PlanBody>
  );
}
